import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Navbar from './components/Navbar';
import Sidebar from './components/Sidebar';
import Login from './pages/Login';
import DynamicDashboard from './pages/DynamicDashboard';
import DossiersList from './pages/DossiersList';
import DossierDetail from './pages/DossierDetail';
import NouveauDossier from './pages/NouveauDossier';
import Registre from './pages/Registre';
import TeamManagement from './pages/TeamManagement';
import WidgetsAdmin from './pages/WidgetsAdmin';
import WebhooksAdmin from './pages/WebhooksAdmin';
import ConfigurationAdmin from './pages/ConfigurationAdmin';
import APIDocumentation from './pages/APIDocumentation';
import ClientForm from './pages/ClientForm';
import ClientDocuments from './pages/ClientDocuments';
import SuperAgent from './pages/SuperAgent';
import Messaging from './pages/Messaging';
import FormulairePublic from './pages/FormulairePublic';
import ParcoursOrganismes from './pages/ParcoursOrganismes';
import AgentIAChat from './components/AgentIAChat';
import './App.css';

const AppLayout = ({ children }) => {
  const { isAuthenticated } = useAuth();
  
  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="flex">
        <Sidebar />
        <main className="flex-1 p-8">
          <div className="max-w-7xl mx-auto">
            {children}
          </div>
        </main>
      </div>
      {/* Assistant IA flottant - visible sur toutes les pages quand connecté */}
      {isAuthenticated && <AgentIAChat isFloating={true} />}
    </div>
  );
};

function AppRoutes() {
  const { isAuthenticated } = useAuth();

  return (
    <Routes>
      <Route 
        path="/login" 
        element={isAuthenticated ? <Navigate to="/" replace /> : <Login />} 
      />
      
      {/* Page publique - Documentation API */}
      <Route path="/docs" element={<APIDocumentation />} />
      
      {/* Page publique - Formulaire client */}
      <Route path="/client/:token" element={<ClientForm />} />
      
      {/* Page publique - Documents client */}
      <Route path="/documents/:token" element={<ClientDocuments />} />
      
      {/* Page publique - Formulaire demande partenaire */}
      <Route path="/demande/:lienUnique" element={<FormulairePublic />} />
      
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AppLayout>
              <DynamicDashboard />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* Route dynamique pour les statuts */}
      <Route
        path="/dossiers/statut/:statutCode"
        element={
          <ProtectedRoute>
            <AppLayout>
              <DossiersList />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/dossiers/corbeille"
        element={
          <ProtectedRoute>
            <AppLayout>
              <DossiersList />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/dossiers/:id"
        element={
          <ProtectedRoute>
            <AppLayout>
              <DossierDetail />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/dossiers"
        element={
          <ProtectedRoute>
            <AppLayout>
              <DossiersList />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/nouveau-dossier"
        element={
          <ProtectedRoute>
            <AppLayout>
              <NouveauDossier />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/registre"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Registre />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/parcours"
        element={
          <ProtectedRoute>
            <AppLayout>
              <ParcoursOrganismes />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/super-admin"
        element={
          <ProtectedRoute requireSuperAdmin>
            <AppLayout>
              <TeamManagement />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/widgets"
        element={
          <ProtectedRoute requireSuperAdmin>
            <AppLayout>
              <WidgetsAdmin />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/webhooks"
        element={
          <ProtectedRoute requireSuperAdmin>
            <AppLayout>
              <WebhooksAdmin />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/configuration"
        element={
          <ProtectedRoute requireSuperAdmin>
            <AppLayout>
              <ConfigurationAdmin />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/super-agent"
        element={
          <ProtectedRoute requireSuperAdmin>
            <AppLayout>
              <SuperAgent />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/messagerie"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Messaging />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
